import React from 'react';
import { Platform, View, TextInput, StyleSheet, type TextStyle } from 'react-native';
import { ms, s, vs } from '../utils/responsive';

interface VerificationCodeInputProps {
  value: string;
  onChangeText: (text: string) => void;
  length?: number;
  cellSpacing?: number;
  isNumeric?: boolean;
}

const VerificationCodeInput: React.FC<VerificationCodeInputProps> = ({
  value,
  onChangeText,
  length = 6,
  cellSpacing = 10,
  isNumeric = true,
}) => {
  const handleChange = (text: string) => {
    const formattedText = isNumeric 
      ? text.replace(/[^0-9]/g, '')
      : text.toUpperCase();
    if (formattedText.length <= length) {
      onChangeText(formattedText);
    }
  };

  const renderCells = () => {
    const cells = [];
    for (let i = 0; i < length; i++) {
      cells.push(
        <View
          key={i}
          style={[
            styles.cell,
            { marginHorizontal: cellSpacing / 2 },
          ]}
        >
          <TextInput
            style={styles.cellText}
            maxLength={1}
            value={value[i] || ''}
            onChangeText={(text) => {
              const newValue =
                value.substring(0, i) + text + value.substring(i + 1);
              handleChange(newValue);
            }}
            keyboardType={isNumeric ? "numeric" : "default"}
            autoCapitalize={isNumeric ? "none" : "characters"}
          />
        </View>
      );
    }
    return cells;
  };

  return (
    <View style={styles.container}>
      <TextInput
        style={styles.hiddenInput}
        value={value}
        onChangeText={handleChange}
        maxLength={length}
        keyboardType={isNumeric ? "numeric" : "default"}
        autoCapitalize={isNumeric ? "none" : "characters"}
      />
      <View style={styles.cellsContainer}>{renderCells()}</View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    alignItems: 'center',
    marginBottom: ms(30),
  },
  hiddenInput: {
    position: 'absolute',
    opacity: 0,
    width: '100%',
    height: s(70),
  },
  cellsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    width: '90%',
  },
  cell: {
    width: s(50),
    height: s(70),
    backgroundColor: '#FFFFFF',
    borderRadius: ms(20),
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: ms(20),
  },
  cellText: {
    fontSize: ms(24, 0.3),
    color: '#2C4364',
    textAlign: 'center',
    // A browser <input> has an intrinsic width (about 20 characters) unless
    // told otherwise, so on web the digit spilled far past its cell and the
    // focus ring drew across the neighbours. Native inputs fill the cell.
    // (`outlineStyle` is a react-native-web extension, hence the cast.)
    ...(Platform.OS === 'web'
      ? ({ width: '100%', height: '100%', outlineStyle: 'none' } as unknown as TextStyle)
      : null),
  },
});

export default VerificationCodeInput;